import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  FileUpload,
  Input,
  useToast,
} from '@/components/common';
import { ExtensionPrompt } from '@/components/ExtensionPrompt';
import { cvService, type ParsedCV } from '@/services/cv.service';
import { profileService } from '@/services/profile.service';
import { profileSyncService } from '@/services/profileSyncService';
import { useAuth } from '@/hooks';
import { apiClient } from '@/services/api';
import type { FullATSScoreV3 } from '@shared/types/ats-report';
import { ExperienceEditor, type Experience } from '@/components/profile/ExperienceEditor';
import { EducationEditor, type Education } from '@/components/profile/EducationEditor';

import { OnboardingStepTrack } from './onboarding/OnboardingStepTrack';
import { ParsingAnimation } from './onboarding/ParsingAnimation';
import { ProfilePreviewCard } from './onboarding/ProfilePreviewCard';
import { ConsentStep } from './onboarding/ConsentStep';
import { CelebrationBurst } from './onboarding/CelebrationBurst';
import './onboarding/onboarding.css';
import { trackOnboardingStep } from '@/services/analytics';

// ── Schema ──────────────────────────────────────

const profileSchema = z.object({
  fullName: z.string().min(1, 'Full name is required'),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  phone: z.string().optional(),
  location: z.string().optional(),
  summary: z.string().optional(),
  skills: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

// ── Helpers ─────────────────────────────────────

const createId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toStr = (v: any): string | null => {
  if (typeof v === 'string' && v.length > 0) return v;
  if (v && typeof v === 'object') {
    const s = v.name || v.language || v.title || v.label;
    if (typeof s === 'string' && s.length > 0) return s;
  }
  return null;
};

const flattenToStrings = (val: any): string[] => {
  if (!val) return [];
  if (typeof val === 'string') return [val];
  if (Array.isArray(val)) return val.map(toStr).filter((s): s is string => s !== null);
  if (typeof val === 'object') {
    return Object.values(val)
      .filter(Array.isArray)
      .flat()
      .map(toStr)
      .filter((s): s is string => s !== null);
  }
  return [];
};

const extractLanguages = (parsed: any): Array<{ language: string; level: string }> => {
  const sources = [parsed?.languages, parsed?.skills?.languages];
  for (const src of sources) {
    if (Array.isArray(src) && src.length > 0) {
      return src
        .map((lang: any) => {
          if (typeof lang === 'string') return { language: lang, level: '' };
          if (lang && typeof lang === 'object') {
            const name = lang.language || lang.name;
            const level = lang.level || lang.proficiency;
            if (typeof name === 'string' && name.length > 0) {
              return { language: name, level: typeof level === 'string' ? level : '' };
            }
          }
          return null;
        })
        .filter((l: any): l is { language: string; level: string } => l !== null);
    }
  }
  return [];
};

const normalizeParsedData = (parsed?: ParsedCV | null): ParsedCV =>
  ({
    personalInfo: {
      firstName: parsed?.personalInfo?.firstName || '',
      lastName: parsed?.personalInfo?.lastName || '',
      email: parsed?.personalInfo?.email,
      phone: parsed?.personalInfo?.phone,
      location: parsed?.personalInfo?.location,
      fullName: (parsed?.personalInfo as any)?.fullName,
    } as any,
    summary: parsed?.summary || '',
    experience: parsed?.experience || [],
    education: parsed?.education || [],
    skills: flattenToStrings(parsed?.skills),
    languages: extractLanguages(parsed),
    certifications: (parsed as any)?.certifications || [],
  }) as any;

/** Count non-empty fields in parsed profile data */
const countExtractedFields = (parsed: ParsedCV): number => {
  let count = 0;
  const pi = parsed.personalInfo;
  if (pi?.firstName) count++;
  if (pi?.lastName) count++;
  if ((pi as any)?.fullName) count++;
  if (pi?.email) count++;
  if (pi?.phone) count++;
  if (pi?.location) count++;
  if (parsed.summary) count++;
  count += parsed.experience?.length || 0;
  count += parsed.education?.length || 0;
  count += Math.min(parsed.skills?.length || 0, 5); // Cap skill count contribution
  return count;
};

// ── Component ───────────────────────────────────

export function OnboardingPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, isAuthenticated } = useAuth();

  // Steps: 0=Upload, 1=Review, 2=Consent, 3=Complete
  const [currentStep, setCurrentStep] = useState(0);
  const [parsedData, setParsedData] = useState<ParsedCV | null>(null);
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [educations, setEducations] = useState<Education[]>([]);
  const [certifications, setCertifications] = useState<any[]>([]);
  const [languages, setLanguages] = useState<any[]>([]);
  const [_projects, setProjects] = useState<any[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [parseMode, setParseMode] = useState<'file' | 'text'>('file');
  const [inputText, setInputText] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);

  // Parallel ATS analysis (runs while user reviews their profile)
  const [atsReport, setAtsReport] = useState<FullATSScoreV3 | null>(null);
  const [atsLoading, setAtsLoading] = useState(false);
  const uploadedFileRef = useRef<File | null>(null);

  // Parsing progress
  const [parseProgress, setParseProgress] = useState(0);
  const [parseElapsed, setParseElapsed] = useState(0);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  // ── Redirect if profile exists ────────────────
  const profileCheckRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || profileCheckRef.current) return;
    profileCheckRef.current = true;

    const checkExistingProfile = async () => {
      try {
        const response = await apiClient.get('/api/v1/profile/exists');
        if (response.data?.data?.exists) {
          await profileService.getProfile();
          navigate('/app/pipeline', { replace: true });
        }
      } catch (err) {
        console.warn('[Onboarding] Profile existence check failed:', err);
      }
    };

    checkExistingProfile();
  }, [isAuthenticated, navigate]);

  // ── Progress helpers ──────────────────────────

  const startProgress = () => {
    setParseProgress(0);
    setParseElapsed(0);

    if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);

    // Smooth progress: fast start, slow toward end
    progressTimerRef.current = setInterval(() => {
      setParseProgress((prev) => {
        if (prev >= 90) return prev; // Hold at 90 until done
        const increment = prev < 30 ? 3 : prev < 60 ? 1.5 : 0.5;
        return Math.min(prev + increment, 90);
      });
    }, 1000);

    elapsedTimerRef.current = setInterval(() => {
      setParseElapsed((prev) => prev + 1);
    }, 1000);
  };

  const stopProgress = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    setParseProgress(100);
  };

  // ── Background ATS analysis ───────────────────

  const analyzeATSFromFile = async (file: File) => {
    setAtsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post<{ data: FullATSScoreV3 }>(
        '/api/v1/cv/analyze-ats/v3/full-from-file',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 20_000 },
      );
      setAtsReport(response.data.data);
    } catch {
      // Silently ignore — ATS analysis failure must not block onboarding
    } finally {
      setAtsLoading(false);
    }
  };

  // ── Apply parsed data ─────────────────────────

  const applyParsedData = (parsed: ParsedCV) => {
    const safeParsed = normalizeParsedData(parsed);
    setParsedData(safeParsed);

    const fullName =
      (safeParsed.personalInfo as any).fullName ||
      `${safeParsed.personalInfo.firstName} ${safeParsed.personalInfo.lastName}`.trim() ||
      '';

    setValue('fullName', fullName);
    setValue('email', safeParsed.personalInfo.email || '');
    setValue('phone', safeParsed.personalInfo.phone || '');
    setValue('location', safeParsed.personalInfo.location || '');
    setValue('summary', safeParsed.summary || '');
    setValue('skills', safeParsed.skills.filter((s) => typeof s === 'string').join(', '));

    setExperiences(
      safeParsed.experience.map((exp) => ({
        id: createId(),
        company: exp.company,
        position: exp.position,
        startDate: exp.startDate,
        endDate: exp.endDate,
        description: exp.description || '',
        current: !exp.endDate,
      }))
    );

    setEducations(safeParsed.education || []);
    setCertifications((safeParsed as any).certifications || []);
    setLanguages(
      (safeParsed.languages || []).map((lang) =>
        typeof lang === 'string' ? { language: lang, level: '' } : lang
      )
    );
    setProjects(((parsed as any)?.additional as any)?.projects || []);

    const hasData =
      fullName ||
      safeParsed.summary ||
      safeParsed.experience.length > 0 ||
      safeParsed.education.length > 0 ||
      safeParsed.skills.length > 0;

    if (!hasData) {
      toast.info('Could not extract data. Please fill in the profile manually.');
    } else {
      toast.success('CV successfully parsed!');
    }

    setTimeout(() => setCurrentStep(1), 400); // Wait for progress bar to fill

    // Kick off parallel ATS analysis (no-op if file not available yet, e.g. text parse)
    if (uploadedFileRef.current) {
      analyzeATSFromFile(uploadedFileRef.current);
    }
  };

  // ── File upload handler ───────────────────────

  const handleFileSelect = async (file: File) => {
    uploadedFileRef.current = file; // Store for parallel ATS analysis
    setIsLoading(true);
    startProgress();

    try {
      const parsed = await cvService.parseCV(file);
      stopProgress();
      applyParsedData(parsed);
    } catch (error) {
      console.error('CV parsing error:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to parse CV. Please try again or fill in manually.'
      );
    } finally {
      stopProgress();
      setIsLoading(false);
    }
  };

  // ── Text parse handler ────────────────────────

  const handleTextParse = async () => {
    if (!inputText.trim()) {
      toast.error('Please paste your resume or LinkedIn profile text.');
      return;
    }
    setIsLoading(true);
    startProgress();

    try {
      const parsed = await cvService.parseText(inputText);
      stopProgress();
      applyParsedData(parsed);
    } catch (error) {
      console.error('CV text parsing error:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to parse text. Please try again or fill in manually.'
      );
    } finally {
      stopProgress();
      setIsLoading(false);
    }
  };

  // ── Extract skills helper (from experience) ───

  const extractSkillsFromProfile = (profile: any): string[] => {
    const allSkills = new Set<string>();

    const experienceDescriptions = (profile.experience || [])
      .map((exp: any) => `${exp.description || ''} ${exp.position || ''} ${exp.company || ''}`.toLowerCase())
      .join(' ');

    const technicalPatterns = [
      /\b(javascript|typescript|python|java|c\+\+|c#|php|ruby|go|rust|swift|kotlin|scala|sql)\b/gi,
      /\b(react|vue|angular|node\.js|express|django|flask|spring|rails|laravel|next\.js)\b/gi,
      /\b(html|css|sass|tailwind|bootstrap)\b/gi,
      /\b(aws|azure|gcp|docker|kubernetes|terraform|jenkins|ci\/cd)\b/gi,
      /\b(git|github|gitlab|jira|confluence)\b/gi,
      /\b(mongodb|mysql|postgresql|redis|elasticsearch)\b/gi,
      /\b(scrum|agile|tdd|devops|microservices)\b/gi,
      /\b(rest|graphql|api|sdk)\b/gi,
    ];

    technicalPatterns.forEach((pattern) => {
      const matches = experienceDescriptions.match(pattern);
      if (matches) {
        matches.forEach((skill: string) => allSkills.add(skill.toLowerCase()));
      }
    });

    return Array.from(allSkills).slice(0, 50);
  };

  // ── Submit profile (Step 1) ───────────────────

  const handleSaveProfile = async (data: ProfileFormData) => {
    setIsLoading(true);

    try {
      const expSource = experiences.length > 0 ? experiences : parsedData?.experience || [];
      const mappedExperience = expSource.map((exp) => ({
        id: (exp as any).id || createId(),
        company: exp.company,
        position: exp.position,
        startDate: exp.startDate,
        endDate: exp.endDate || null,
        description: exp.description || '',
        current: 'current' in exp ? Boolean((exp as any).current) : !exp.endDate,
      }));

      const mappedEducation = (educations || []).map((edu) => ({
        id: edu.id || createId(),
        institution: edu.institution,
        degree: edu.degree,
        fieldOfStudy: edu.field || '',
        startDate: edu.startDate,
        endDate: edu.endDate || null,
        current: !edu.endDate,
      }));

      // Combine AI-parsed skills with extracted skills
      let skillsFromAI: string[] = [];
      if (parsedData?.skills && Array.isArray(parsedData.skills)) {
        skillsFromAI = parsedData.skills.filter((s: any) => typeof s === 'string');
      } else if (parsedData?.skills && typeof parsedData.skills === 'object') {
        const skills = parsedData.skills as any;
        skillsFromAI = [
          ...(Array.isArray(skills.technical) ? skills.technical.filter((s: any) => typeof s === 'string') : []),
          ...(Array.isArray(skills.soft) ? skills.soft.filter((s: any) => typeof s === 'string') : []),
        ];
      }

      const skillsFromForm = data.skills?.split(',').map((s) => s.trim()).filter(Boolean) || [];
      const skillsFromExperience = extractSkillsFromProfile({ experience: mappedExperience, education: mappedEducation });
      const allSkills = Array.from(new Set([...skillsFromAI, ...skillsFromForm, ...skillsFromExperience])).slice(0, 100);

      const profileData = {
        firstName: data.fullName.split(/\s+/)[0] || data.fullName,
        lastName: data.fullName.split(/\s+/).slice(1).join(' ') || '',
        email: data.email || '',
        phone: data.phone || null,
        location: data.location || null,
        summary: data.summary || null,
        experience: mappedExperience,
        education: mappedEducation,
        skills: allSkills,
        certifications: (certifications || []).map((cert) => ({
          id: createId(),
          name: cert.name || '',
          issuer: cert.issuer || '',
          issueDate: cert.date || '',
          expiryDate: cert.expirationDate || cert.expiryDate || null,
          credentialId: cert.credentialId || null,
        })),
        languages: (languages || []).map((lang) => (typeof lang === 'string' ? lang : lang.language)),
        userId: 'local-user',
        id: '',
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      const result = await profileService.createProfile(profileData);
      profileSyncService.notifyProfileSaved(result);

      toast.success('Profile created!');
      setCurrentStep(2); // Move to consent step
    } catch (error) {
      console.error('Profile creation error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create profile');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Quick save (preview mode, no edits) ───────

  const handleQuickSave = () => {
    handleSubmit(handleSaveProfile)();
  };

  // ── Skip ──────────────────────────────────────

  const handleSkip = () => {
    toast.info('You can create your profile later.');
    navigate('/app/pipeline');
  };

  // ── Derived data for preview ──────────────────

  const fullName =
    parsedData
      ? (parsedData.personalInfo as any)?.fullName ||
        `${parsedData.personalInfo?.firstName || ''} ${parsedData.personalInfo?.lastName || ''}`.trim()
      : '';

  const jobTitle = parsedData?.experience?.[0]?.position;
  const location = parsedData?.personalInfo?.location;
  const skillsList = parsedData?.skills
    ? Array.isArray(parsedData.skills)
      ? parsedData.skills.filter((s): s is string => typeof s === 'string')
      : []
    : [];
  const fieldsExtracted = parsedData ? countExtractedFields(parsedData) : 0;
  const userEmail = user?.email || parsedData?.personalInfo?.email || '';
  const firstName = fullName.split(/\s+/)[0] || 'there';

  // Track onboarding step changes
  useEffect(() => {
    trackOnboardingStep(currentStep);
  }, [currentStep]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      profileSyncService.cleanup();
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
    };
  }, []);

  // ── Render ────────────────────────────────────

  return (
    <div className="onboarding-shell">
      {/* Brand */}
      <div className="onboarding-shell__brand">
        <div className="onboarding-shell__brand-mark">
          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </div>
        ATS CV Checker
      </div>

      {/* Step Track */}
      <OnboardingStepTrack currentStep={currentStep} totalSteps={4} />

      {/* Card */}
      <div className="onboarding-card">
        {/* ════ Step 0: Upload CV ════ */}
        {currentStep === 0 && !isLoading && (
          <div className="onboarding-step" key="step-0">
            {/* Illustration */}
            <div style={{ textAlign: 'center', marginBottom: 'var(--space-4)' }}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                <rect x="14" y="6" width="36" height="48" rx="4" stroke="var(--color-primary)" strokeWidth="2" fill="color-mix(in srgb, var(--color-primary) 8%, transparent)" />
                <line x1="22" y1="20" x2="42" y2="20" stroke="var(--color-border-strong)" strokeWidth="2" strokeLinecap="round" />
                <line x1="22" y1="28" x2="38" y2="28" stroke="var(--color-border-strong)" strokeWidth="2" strokeLinecap="round" />
                <line x1="22" y1="36" x2="34" y2="36" stroke="var(--color-border-strong)" strokeWidth="2" strokeLinecap="round" />
                <circle cx="48" cy="14" r="8" fill="var(--color-primary)" opacity="0.2" />
                <path d="M45 14l2 2 4-4" stroke="var(--color-primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            <h2 className="onboarding-card__headline">Let's Build Your Profile</h2>
            <p className="onboarding-card__subline">
              Upload your CV and we'll extract your experience, skills, and education automatically.
            </p>

            {/* Parse mode toggle */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setParseMode('file')}
                className={`px-4 py-2.5 rounded-md border transition-colors text-sm font-medium ${
                  parseMode === 'file'
                    ? 'border-primary text-primary bg-primary-soft'
                    : 'border-border text-text-primary hover:border-primary-soft hover:bg-primary-soft'
                }`}
              >
                Upload CV
              </button>
              <button
                type="button"
                onClick={() => setParseMode('text')}
                className={`px-4 py-2.5 rounded-md border transition-colors text-sm font-medium ${
                  parseMode === 'text'
                    ? 'border-primary text-primary bg-primary-soft'
                    : 'border-border text-text-primary hover:border-primary-soft hover:bg-primary-soft'
                }`}
              >
                Paste Text
              </button>
            </div>

            {/* Upload or text area */}
            {parseMode === 'file' ? (
              <FileUpload onFileSelect={handleFileSelect} disabled={isLoading} />
            ) : (
              <div className="space-y-3">
                <textarea
                  rows={6}
                  placeholder="Paste your resume or LinkedIn profile text here"
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                />
                <p className="text-xs text-text-muted">
                  Keep headings like Summary, Experience, Skills. Use a consistent date format.
                </p>
                <Button onClick={handleTextParse} isLoading={isLoading}>
                  Parse Text
                </Button>
              </div>
            )}

            {/* Skip */}
            <div className="pt-6">
              <button type="button" onClick={handleSkip} className="onboarding-skip-link">
                Skip for now
                <span className="onboarding-skip-link__arrow">&rarr;</span>
              </button>
              <p className="text-xs text-text-muted mt-1">
                You can add your details manually later.
              </p>
            </div>
          </div>
        )}

        {/* ════ Step 0: Parsing State ════ */}
        {currentStep === 0 && isLoading && (
          <ParsingAnimation progress={parseProgress} elapsedSeconds={parseElapsed} />
        )}

        {/* ════ Step 1: Review Profile ════ */}
        {currentStep === 1 && parsedData && (
          <div className="onboarding-step" key="step-1">
            <h2 className="onboarding-card__headline">Review Your Profile</h2>
            <p className="onboarding-card__subline">
              Here's what we found. Make corrections if needed.
            </p>

            {/* Preview Card */}
            <ProfilePreviewCard
              fullName={fullName}
              jobTitle={jobTitle}
              location={location || undefined}
              skills={skillsList}
              experience={(parsedData.experience || []).map((exp) => ({
                company: exp.company,
                position: exp.position,
                startDate: exp.startDate,
                endDate: exp.endDate,
              }))}
              education={(parsedData.education || []).map((edu) => ({
                institution: edu.institution,
                degree: edu.degree,
              }))}
              fieldsExtracted={fieldsExtracted}
              atsReport={atsReport ?? undefined}
              atsLoading={atsLoading}
            />

            {/* Edit Form (expanded) */}
            <div className={`edit-form-container${isEditMode ? ' edit-form-container--open' : ''}`}>
              {isEditMode && (
                <div className="space-y-4 pt-4">
                  <Input label="Full Name *" error={errors.fullName?.message} fullWidth {...register('fullName')} />
                  <Input label="Email" type="email" error={errors.email?.message} fullWidth {...register('email')} />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input label="Phone" error={errors.phone?.message} fullWidth {...register('phone')} />
                    <Input label="Location" error={errors.location?.message} fullWidth {...register('location')} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Professional Summary</label>
                    <textarea
                      rows={3}
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      {...register('summary')}
                    />
                  </div>

                  <ExperienceEditor experiences={experiences} onChange={setExperiences} disabled={isLoading} />
                  <EducationEditor education={educations} onChange={setEducations} disabled={isLoading} />

                  {/* Certifications */}
                  {certifications.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Certifications ({certifications.length})
                      </label>
                      <div className="space-y-2">
                        {certifications.map((cert, i) => (
                          <div key={i} className="flex items-start gap-2 p-3 border border-border rounded-md bg-surface">
                            <div className="flex-1 space-y-2">
                              <input
                                type="text"
                                value={cert.name || ''}
                                onChange={(e) => {
                                  const updated = [...certifications];
                                  updated[i] = { ...updated[i], name: e.target.value };
                                  setCertifications(updated);
                                }}
                                placeholder="Certification name"
                                className="w-full px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                              />
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={cert.issuer || ''}
                                  onChange={(e) => {
                                    const updated = [...certifications];
                                    updated[i] = { ...updated[i], issuer: e.target.value };
                                    setCertifications(updated);
                                  }}
                                  placeholder="Issuer"
                                  className="flex-1 px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                                />
                                <input
                                  type="text"
                                  value={cert.date || ''}
                                  onChange={(e) => {
                                    const updated = [...certifications];
                                    updated[i] = { ...updated[i], date: e.target.value };
                                    setCertifications(updated);
                                  }}
                                  placeholder="Date"
                                  className="w-24 px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                                />
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setCertifications(certifications.filter((_, j) => j !== i))}
                              className="text-text-secondary hover:text-danger text-sm px-1"
                              title="Remove"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Languages */}
                  {languages.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Languages ({languages.length})
                      </label>
                      <div className="space-y-2">
                        {languages.map((lang, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={typeof lang === 'string' ? lang : lang.language || ''}
                              onChange={(e) => {
                                const updated = [...languages];
                                const current = typeof updated[i] === 'string' ? { language: updated[i], level: '' } : { ...updated[i] };
                                current.language = e.target.value;
                                updated[i] = current;
                                setLanguages(updated);
                              }}
                              placeholder="Language"
                              className="flex-1 px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                            />
                            <input
                              type="text"
                              value={typeof lang === 'string' ? '' : lang.level || ''}
                              onChange={(e) => {
                                const updated = [...languages];
                                const current = typeof updated[i] === 'string' ? { language: updated[i], level: '' } : { ...updated[i] };
                                current.level = e.target.value;
                                updated[i] = current;
                                setLanguages(updated);
                              }}
                              placeholder="Level"
                              className="w-28 px-2 py-1 text-sm border border-border rounded bg-background text-text-primary"
                            />
                            <button
                              type="button"
                              onClick={() => setLanguages(languages.filter((_, j) => j !== i))}
                              className="text-text-secondary hover:text-danger text-sm px-1"
                              title="Remove"
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">Skills (comma-separated)</label>
                    <textarea
                      rows={2}
                      placeholder="JavaScript, React, Node.js, ..."
                      className="w-full px-3 py-2 border border-border rounded-md bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                      {...register('skills')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons — always visible regardless of edit mode */}
            <div className="space-y-3 pt-4">
              <Button onClick={handleQuickSave} isLoading={isLoading} fullWidth>
                {isEditMode ? 'Save Changes' : 'Looks Good'}
              </Button>
              <button
                type="button"
                onClick={() => setIsEditMode(!isEditMode)}
                className="w-full text-center text-sm text-text-secondary hover:text-primary transition-colors py-2"
              >
                {isEditMode ? 'Cancel Editing' : 'Edit Details'}
              </button>
            </div>
          </div>
        )}

        {/* ════ Step 2: Email Consent ════ */}
        {currentStep === 2 && (
          <ConsentStep
            email={userEmail}
            isAuthenticated={isAuthenticated}
            onContinue={() => setCurrentStep(3)}
          />
        )}

        {/* ════ Step 3: You're All Set ════ */}
        {currentStep === 3 && (
          <div className="onboarding-step" key="step-3">
            <CelebrationBurst />

            <h2 className="onboarding-card__headline">You're Ready to Go, {firstName}!</h2>
            <p className="onboarding-card__subline">
              Your profile is set up. Now let's see how well your CV performs.
            </p>

            {/* Next steps */}
            <div className="next-steps-grid">
              <button
                type="button"
                className="next-step-card"
                onClick={() => navigate('/app/dashboard')}
              >
                <div className="next-step-card__icon">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zm6-4a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zm6-3a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                </div>
                <div className="next-step-card__label">Check ATS Score</div>
                <div className="next-step-card__sublabel">See how your CV scores against jobs</div>
              </button>

              <button
                type="button"
                className="next-step-card"
                onClick={() => navigate('/app/pipeline')}
              >
                <div className="next-step-card__icon">
                  <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="next-step-card__label">Add Your First Job</div>
                <div className="next-step-card__sublabel">Paste a job description and match</div>
              </button>
            </div>

            <Button onClick={() => navigate('/app/pipeline')} variant="primary" fullWidth>
              Go to Dashboard
            </Button>

            {/* Extension prompt */}
            <div className="mt-6">
              <ExtensionPrompt variant="post-onboarding" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
